import {
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useTransition,
} from 'react';
import { Select, iOption } from "../../../../../core/components/atoms/Select";
import MultipleSelect, { IMultipleOptions } from "../../../../../core/components/atoms/MultipleSelect";
import { Typography } from "../../../../../core/components/molecules/Typography";
import { practiceConfigStore } from "../../../stores/practiceConfigStore";
import { Input } from "../../../../../core/components/atoms/Input";
import { SectionContainer } from "../../../components/SectionContainer";
import Checkbox from "../../../../../core/components/atoms/Checkbox";
import { useFetchExercisesByInstitutions } from "../../../../../core/hooks/useFetchExercisesByInstitutions";
import { listExercisesYear } from '../../../../Disciplines/utils/exerciseHelper';

type InstitutionsOption = {
  value: string | number;
  label: string;
  quantity?: number;
};

type FetchInstitutionsResult = {
  isLoading: boolean;
  data?: {
    exercisesQuantity?: number;
    exercisesByInstitutions?: InstitutionsOption[];
  };
};
type Difficulty = "easy" | "medium" | "hard";

const QUESTION_TYPE_OPTIONS: Readonly<iOption[]> = [
  { label: "Vestibulares", value: "vestibular" },
  { label: "Questões inéditas", value: "inedit" },
  { label: "Enem", value: "enem" },
  { label: "Enem e Vestibulares", value: "both" },
] as const;

const ALL_OPTION: iOption = { label: "Todas", value: "all" };

export function QuestionSection({ setAvailableQuestions }: { setAvailableQuestions: (value: number) => void }) {
  const yearOptionsRaw = listExercisesYear();
  const yearOptions: IMultipleOptions[] = useMemo(() =>
    yearOptionsRaw
      .filter((opt) => opt.value !== 'all')
      .map((opt) => ({ value: String(opt.value), label: opt.label })),
    [yearOptionsRaw]
  );
  // Mantive como estava, caso não use shallow/seletores no store:
  const {
    setHasVideoResolution,
    setDifficulty,
    setSimulationType,
    setHasntDone,
    setTitle,
    title,
    difficulty,
    hasVideoResolution,
    hasntDone,
    setInstitutionType,
    setQuestionsNumber,
    institutionType,
    simulationType,
    selectedYear,
    setSelectedYear,
    areasFilter,
    courseId,
    courseWeekId,
  } = practiceConfigStore();
  const selectedQuestionType: iOption =
    QUESTION_TYPE_OPTIONS.find((o) => o.value === simulationType) ??
    QUESTION_TYPE_OPTIONS[0];

  const fetchParams = useMemo(() => ({
    areasFilter,
    courseId,
    courseWeekId,
    year: selectedYear.length > 0 ? selectedYear : undefined,
    difficulty,
    hasVideoResolution,
    hasntDone,
    simulationType: String(selectedQuestionType.value),
    institutionType: institutionType.length > 0 ? institutionType : undefined,
    enabled: true,
  }), [
    areasFilter,
    courseId,
    courseWeekId,
    selectedQuestionType.value,
    selectedYear,
    difficulty,
    hasVideoResolution,
    hasntDone,
  ]);

  const fetchInstitutions = useFetchExercisesByInstitutions(fetchParams);

  const institutionsOptions = useMemo<IMultipleOptions[]>(() => {
    const exercisesByInstitutions = fetchInstitutions.data?.exercisesByInstitutions ?? [];
    return exercisesByInstitutions.map((inst: InstitutionsOption) => ({
      value: String(inst.value),
      label: inst.label,
    }));
  }, [fetchInstitutions.data]);

  useEffect(() => {
    setSimulationType(String(selectedQuestionType.value));
    setInstitutionType([]);
  }, []);

  const [, startTransition] = useTransition();

  const onChangeQuestionType = useCallback(
    (value: iOption) => {
      startTransition(() => {
        setSimulationType(String(value.value));
        // Sempre que troca o tipo, resetar campos relacionados sem bloquear a UI:
        setQuestionsNumber("");
        setInstitutionType([]);
      });
    },
    [setSimulationType, setQuestionsNumber, setInstitutionType]
  );

  const onChangeYear = useCallback(
    (values: string[]) => {
      startTransition(() => {
        setSelectedYear(values);
        setInstitutionType([]);
        setQuestionsNumber("");
      });
    },
    [setSelectedYear, setInstitutionType, setQuestionsNumber]
  )
  const onChangeInstitution = useCallback(
    (values: string[]) => {
      startTransition(() => {
        setInstitutionType(values);
        setQuestionsNumber("");
      });
    },
    [setInstitutionType, setQuestionsNumber]
  );

  const toggleDifficulty = useCallback(
    (value: Difficulty) => {
      // Evita mutação acidental e re-render desnecessário
      const set = new Set<string>(difficulty);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      setDifficulty(Array.from(set));
    },
    [difficulty, setDifficulty]
  );

  const onToggleVideoResolution = useCallback(() => {
    setHasVideoResolution(!hasVideoResolution);
  }, [hasVideoResolution, setHasVideoResolution]);

  const onToggleHasntDone = useCallback(() => {
    setHasntDone(!hasntDone);
  }, [hasntDone, setHasntDone]);

  const titleInputId = useId();
  const videoId = useId();
  const notDoneId = useId();

  function getInstitutionsHelperText(fetchInstitutions: FetchInstitutionsResult): string {
    if (fetchInstitutions.isLoading) {
      return 'Carregando instituições…';
    }

    const exercisesByInstitutions = fetchInstitutions.data ? fetchInstitutions.data.exercisesByInstitutions : [];
    let totalExercises = 0;

    if(!exercisesByInstitutions || exercisesByInstitutions.length === 0) {
      setAvailableQuestions(0);
      return 'Não foram encontradas questões para o filtro selecionado.';
    }

    if(selectedQuestionType.value === 'vestibular') {
      const filtered = institutionType.length > 0 ? exercisesByInstitutions.filter((it) => institutionType.includes(it.value.toString())) : exercisesByInstitutions;
      totalExercises = filtered.reduce((acc, curr) => acc + (curr.quantity ?? 0), 0);
    }

    if(selectedQuestionType.value === 'both') {
      const filtered = institutionType.length > 0 ? exercisesByInstitutions.filter((it) => institutionType.includes(it.value.toString())) : exercisesByInstitutions;
      totalExercises = filtered.reduce((acc, curr) => acc + (curr.quantity ?? 0), 0);
    }

    if(selectedQuestionType.value === 'enem') {
      totalExercises = fetchInstitutions.data?.exercisesQuantity ?? 0;
    }

    if(selectedQuestionType.value === 'inedit') {
      totalExercises = fetchInstitutions.data?.exercisesQuantity ?? 0;
    }

    setAvailableQuestions(totalExercises);
    return 'Questões disponíveis: ' + totalExercises
  }

  // const hasEnem = selectedQuestionType.value !== 'vestibular' && selectedQuestionType.value !== 'both';

  return (
    <>
      {/*<Input*/}
      {/*  id={titleInputId}*/}
      {/*  label="Nome do simulado"*/}
      {/*  value={title}*/}
      {/*  onChange={(e) => setTitle(e.target.value)}*/}
      {/*  aria-describedby={`${titleInputId}-desc`}*/}
      {/*/>*/}
      <SectionContainer
        title="Questões"
        warning="O simulado terá apenas questões objetivas. Não são inclusas questões discursivas ou de somatória."
      >
        <DifficultySelector
          selected={new Set(difficulty)}
          onToggle={toggleDifficulty}
        />

        <Checkbox
          onChange={onToggleVideoResolution}
          label="Incluir apenas questões com resolução em vídeo"
          checked={hasVideoResolution}
          id={videoId}
          value="with-video"
          variant="rounded"
          aria-describedby={`${videoId}-desc`}
        />

        <Checkbox
          onChange={onToggleHasntDone}
          label="Incluir apenas questões não respondidas"
          checked={hasntDone}
          id={notDoneId}
          value="not-done"
          variant="rounded"
          aria-describedby={`${notDoneId}-desc`}
        />
        <div className="flex flex-col gap-xs">
          <div className="xs:w-full lg:w-3/4 flex flex-row gap-xl">
            <Select
              options={QUESTION_TYPE_OPTIONS as iOption[]}
              label="Usar questões de"
              aria-label="Origem das questões"
              onChange={onChangeQuestionType}
              value={selectedQuestionType}
            />
            {selectedQuestionType?.value && (
              <MultipleSelect
                options={yearOptions}
                label='Selecione o ano'
                placeholder='Todos os anos'
                onChange={onChangeYear}
                selected={selectedYear}
                className="xs:w-full lg:w-2/4"
              />
            )}
            {(selectedQuestionType.value === "vestibular" ||
              selectedQuestionType.value === "both") && (
              <MultipleSelect
                options={institutionsOptions}
                onChange={onChangeInstitution}
                selected={institutionType}
                label="Filtrar por instituição"
                placeholder='Todas as instituições'
                disabled={fetchInstitutions.isLoading || !!fetchInstitutions.error}
              />
            )}
          </div>
          {selectedQuestionType.value && (
            <Typography size='small' className='text-mono-color-black'>
              {getInstitutionsHelperText(fetchInstitutions)}
            </Typography>
          )}
        </div>
      </SectionContainer>
    </>
  );
}

/** =========================
 *  Presentational subview
 * ========================== */
const DifficultySelector = memo(function DifficultySelector(props: {
  selected: Set<string>;
  onToggle: (d: Difficulty) => void;
}) {
  return (
    <div className="flex flex-col gap-md">
      <Typography as="h3">Grau de dificuldade das questões</Typography>
      <div className="flex flex-row gap-xl" role="group" aria-label="Dificuldade">
        <Checkbox
          onChange={() => props.onToggle("easy")}
          label="Fácil"
          checked={props.selected.has("easy")}
          id="practice-easy"
          value="easy"
          variant="rounded"
        />
        <Checkbox
          onChange={() => props.onToggle("medium")}
          label="Médio"
          checked={props.selected.has("medium")}
          id="practice-medium"
          value="medium"
          variant="rounded"
        />
        <Checkbox
          onChange={() => props.onToggle("hard")}
          label="Difícil"
          checked={props.selected.has("hard")}
          id="practice-hard"
          value="hard"
          variant="rounded"
        />
      </div>
    </div>
  );
});
